import React, { useState, useEffect } from "react";
import { MessageCircle, Phone, Sparkles, GraduationCap, Star, Zap, Clock } from "lucide-react";
import { useCMS } from "@/context/CMSContext";

const FixedCTA = () => {
  const { content } = useCMS();
  const contact = content.contact;
  const [showUrgency, setShowUrgency] = useState(true);
  
  // Get phone number from CMS or use default
  const phoneNumber = (contact.whatsapp || contact.phone || "+917055547000").replace(/\s/g, "").replace("+", "");
  const whatsappMessage = encodeURIComponent(
    "Hi! I saw your 97% placement rate at Taj, Oberoi & ITC Hotels. I want to know about admissions for 2026 batch. Please call me back."
  );

  // Toggle urgency banner
  useEffect(() => {
    const interval = setInterval(() => {
      setShowUrgency(prev => !prev);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleWhatsAppClick = () => {
    if (window.trackClickConversion) {
      window.trackClickConversion('whatsapp_click', 'Mobile CTA - WhatsApp');
    }
  };

  const handleCallClick = () => {
    if (window.trackClickConversion) {
      window.trackClickConversion('phone_call', 'Mobile CTA - Call');
    }
  };

  const handleApplyClick = (e) => {
    e.preventDefault();
    if (window.trackClickConversion) {
      window.trackClickConversion('apply_click', 'Mobile CTA - Apply Now');
    }
    // Scroll to callback form after Oberoi section
    const callbackSection = document.getElementById('callback-after-oberoi');
    if (callbackSection) {
      callbackSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBannerClick = () => {
    if (window.trackClickConversion) {
      window.trackClickConversion('banner_click', 'Mobile CTA - Request Callback Banner');
    }
    // Scroll to Request Callback section
    const callbackSection = document.querySelector('[data-testid="request-callback-section"]');
    if (callbackSection) {
      callbackSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // Fallback: scroll to enquiry section
      const enquirySection = document.getElementById('enquiry');
      if (enquirySection) {
        enquirySection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden" data-testid="fixed-cta">
      {/* Urgency Banner - Scrolls to Request Callback */}
      <button 
        onClick={handleBannerClick}
        className="w-full bg-gradient-to-r from-[hsl(43,74%,45%)] to-[hsl(43,74%,55%)] text-white py-2 px-3 overflow-hidden cursor-pointer hover:from-[hsl(43,74%,50%)] hover:to-[hsl(43,74%,60%)] transition-all"
        data-testid="cta-banner"
      >
        <div className="flex items-center justify-center gap-2 text-xs font-bold">
          {showUrgency ? (
            <>
              <Zap className="w-3.5 h-3.5 animate-pulse" fill="currentColor" />
              <span>🔥 Only 47 Seats Left for 2026 - TAP TO APPLY!</span>
              <Zap className="w-3.5 h-3.5 animate-pulse" fill="currentColor" />
            </>
          ) : (
            <>
              <Star className="w-3.5 h-3.5" fill="currentColor" />
              <span>⭐ 97% Placement | Taj, Oberoi, ITC Hotels → APPLY</span>
              <Star className="w-3.5 h-3.5" fill="currentColor" />
            </>
          )}
        </div>
      </button>
      
      {/* Background with blur */}
      <div className="bg-white/95 backdrop-blur-md border-t border-[hsl(345,20%,85%)] shadow-lg">
        <div className="flex">
          {/* WhatsApp Button - Enhanced */}
          <a
            href={`https://wa.me/${phoneNumber}?text=${whatsappMessage}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleWhatsAppClick}
            className="flex-1 flex flex-col items-center justify-center py-2.5 bg-[#25D366] text-white hover:bg-[#20BD5A] transition-colors relative overflow-hidden"
            data-testid="cta-whatsapp"
          >
            <div className="flex items-center gap-1.5">
              <MessageCircle className="w-4 h-4" />
              <span className="font-bold text-xs">WhatsApp</span>
            </div>
            <span className="text-[10px] opacity-90">Reply in 2 mins</span>
            {/* Pulse effect */}
            <span className="absolute top-1 right-1 w-2 h-2 bg-white rounded-full animate-ping" />
          </a>

          {/* Call Button - Enhanced */}
          <a
            href={`tel:+${phoneNumber}`}
            onClick={handleCallClick}
            className="flex-1 flex flex-col items-center justify-center py-2.5 bg-[#007bff] text-white hover:bg-[#0056b3] transition-colors relative"
            data-testid="cta-call"
          >
            <div className="flex items-center gap-1.5">
              <Phone className="w-4 h-4 animate-pulse" />
              <span className="font-bold text-xs">Call Now</span>
            </div>
            <span className="text-[10px] opacity-90">Talk to Counselor</span>
          </a>

          {/* Apply Now Button - Most Prominent */}
          <button
            onClick={handleApplyClick}
            className="flex-[1.4] flex flex-col items-center justify-center py-2.5 bg-gradient-to-r from-[hsl(345,60%,35%)] to-[hsl(345,60%,45%)] text-white hover:from-[hsl(345,60%,40%)] hover:to-[hsl(345,60%,50%)] transition-all relative overflow-hidden"
            data-testid="cta-apply"
          >
            <div className="flex items-center gap-1.5">
              <GraduationCap className="w-4 h-4" />
              <span className="font-black text-xs tracking-wide">APPLY NOW</span>
            </div>
            <span className="text-[10px] font-medium bg-[hsl(43,74%,50%)] px-2 rounded-full mt-0.5">Attractive Scholarships</span>
            {/* Shine effect */}
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shine" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Desktop Floating WhatsApp Button - Enhanced
export const FloatingWhatsApp = () => {
  const { content } = useCMS();
  const contact = content.contact;
  
  const phoneNumber = (contact.whatsapp || contact.phone || "+917055547000").replace(/\s/g, "").replace("+", "");
  const whatsappMessage = encodeURIComponent(
    "Hi! I saw your 97% placement rate at Taj, Oberoi & ITC Hotels. I'm interested in admission for 2026 batch. Please share details."
  );

  const handleWhatsAppClick = () => {
    if (window.trackClickConversion) {
      window.trackClickConversion('whatsapp_click', 'Desktop Floating - WhatsApp');
    }
  };

  return (
    <a
      href={`https://wa.me/${phoneNumber}?text=${whatsappMessage}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleWhatsAppClick}
      className="hidden lg:flex fixed bottom-8 right-8 z-50 items-center gap-3 bg-[#25D366] text-white pl-5 pr-4 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
      data-testid="floating-whatsapp"
    >
      <div className="flex flex-col">
        <span className="font-bold text-sm">Chat with us</span>
        <span className="text-[10px] opacity-90 flex items-center gap-1">
          <Clock className="w-3 h-3" /> Reply in 2 mins
        </span>
      </div>
      <div className="relative">
        <MessageCircle className="w-7 h-7" />
        {/* Online indicator */}
        <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-white rounded-full border-2 border-[#25D366]" />
      </div>
      {/* Pulse animation */}
      <span className="absolute -inset-1 rounded-full bg-[#25D366] animate-ping opacity-20" />
    </a>
  );
};

// Desktop Apply Now Floating Button - Enhanced
export const FloatingApply = () => {
  const handleApplyClick = (e) => {
    e.preventDefault();
    if (window.trackClickConversion) {
      window.trackClickConversion('apply_click', 'Desktop Floating - Apply');
    }
    // Scroll to callback form after Oberoi section
    const callbackSection = document.getElementById('callback-after-oberoi');
    if (callbackSection) {
      callbackSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <button
      onClick={handleApplyClick}
      className="hidden lg:flex fixed bottom-8 left-8 z-50 items-center gap-3 bg-gradient-to-r from-[hsl(345,60%,35%)] to-[hsl(345,60%,45%)] text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
      data-testid="floating-apply"
    >
      <GraduationCap className="w-6 h-6" />
      <div className="flex flex-col">
        <span className="font-bold text-sm">Apply for 2026</span>
        <span className="text-[10px] opacity-90">97% Placement Rate</span>
      </div>
      <span className="bg-[hsl(43,74%,50%)] text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1">
        <Zap className="w-3 h-3" /> 47 Seats Left
      </span>
      {/* Shine effect */}
      <span className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
    </button>
  );
};

export default FixedCTA;
